var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useMemo } from "react";
import { Combobox as HuiCombobox, ComboboxInput, ComboboxButton, ComboboxOptions, } from "@headlessui/react";
import { cn } from "../../lib/cn";
import { ChevronsUpDownIcon, SquareCheckBigIcon, SquareIcon } from "lucide-react";
/**
 * A custom combobox component.
 *
 * @component
 * @example
 * <Combobox
 *   searchValues={allSearchableValues}
 *   selectedItems={stateValues}
 *   setSelectedValues={(newValues) => setState(newValues)}
 *   className="custom-combobox"
 *   placeholder="Select items"
 * />
 *
 * @param {Object} props - The component props.
 * @param {Array<{ displayName: string, value: string|number }>} props.searchValues - The array of search values.
 * @param {Array} props.selectedItems - The array of selected items. This includes the display name and value.
 * @param {Function} props.setSelectedValues - The function to set the selected values.
 * @param {string} [props.className] - The optional class name for styling.
 * @param {string} [props.placeholder] - The optional placeholder text.
 * @returns {JSX.Element} The rendered Combobox component.
 */
export var Combobox = function (_a) {
    var searchValues = _a.searchValues, selectedItems = _a.selectedItems, setSelectedValues = _a.setSelectedValues, className = _a.className, props = __rest(_a, ["searchValues", "selectedItems", "setSelectedValues", "className"]);
    var _b = useState(''), query = _b[0], setQuery = _b[1];
    var filteredResults = useMemo(function () {
        if (!query)
            return searchValues;
        return searchValues.filter(function (_a) {
            var displayName = _a.displayName;
            return displayName.toLowerCase().includes(query.toLowerCase());
        });
    }, [query, searchValues, selectedItems]);
    /**
     * Handles the selection change in the Combobox.
     *
     * This function takes the new selected values and combines them with the
     * already selected values, adding new selections and removing deselections.
     *
     * @param {Array<string | number>} newValues - The new selected values.
     */
    var handleSelectionChange = function (newValues) {
        var alreadySelectedValues = selectedItems.map(function (_a) {
            var value = _a.value;
            return value;
        });
        var updatedSelectedValues = alreadySelectedValues.filter(function (value) { return !newValues.includes(value); });
        var newSelectedValues = newValues.filter(function (value) { return !alreadySelectedValues.includes(value); });
        var selectedValues = __spreadArray(__spreadArray([], updatedSelectedValues, true), newSelectedValues, true);
        setSelectedValues(selectedValues);
    };
    return (React.createElement(HuiCombobox, { multiple: true, as: 'div', className: cn('tw-relative', className), defaultValue: selectedItems, onChange: handleSelectionChange, onClose: function () { return setQuery(''); } },
        React.createElement(ComboboxInput, { "aria-label": "Search", className: cn('tw-flex tw-h-[3.125rem] tw-w-full tw-rounded-[0.25rem] tw-border tw-px-3 tw-py-2 tw-text-sm tw-relative tw-bg-transparent tw-border-[#ced4da] data-[focus]:tw-ring-2 data-[focus]:tw-ring-[#a1cbef] data-[focus]:tw-border-[#a1cbef] tw-text-neutral-700 focus-visible:!tw-outline-none', selectedItems.length > 0 ? 'placeholder:tw-text-green-700 ' : 'placeholder:tw-text-neutral-500/85'), onChange: function (e) { return setQuery(e.target.value); }, placeholder: selectedItems.length > 0 ? "".concat(selectedItems.length, " item(s) geselecteerd") : props.placeholder }),
        React.createElement(ComboboxButton, { className: 'tw-absolute tw-right-[0.75rem] tw-bottom-[0.7rem]' },
            React.createElement(ChevronsUpDownIcon, { className: 'tw-w-[18px] tw-h-[18px] tw-text-neutral-800' })),
        React.createElement(ComboboxOptions, { className: 'tw-absolute tw-w-full tw-mt-2 tw-px-3 tw-py-2 tw-rounded-md tw-bg-white tw-border tw-border-neutral-700 dark:tw-border-neutral-300 tw-shadow-md tw-flex tw-gap-y-1.5 tw-flex-col empty:tw-hidden tw-z-10 tw-max-h-80 tw-overflow-y-auto' }, filteredResults.map(function (_a) {
            var displayName = _a.displayName, value = _a.value;
            var isSelected = selectedItems.some(function (_a) {
                var selectedValue = _a.value;
                return selectedValue === value;
            });
            return (React.createElement("div", { key: value, "aria-checked": isSelected, onClick: function () { return handleSelectionChange([value]); }, className: 'tw-flex tw-items-center tw-gap-x-2 tw-cursor-pointer tw-group aria-checked:tw-bg-neutral-100 tw-rounded-md' },
                React.createElement("div", null, isSelected
                    ? React.createElement(SquareCheckBigIcon, { className: 'tw-size-4 tw-text-neutral-800' })
                    : React.createElement(SquareIcon, { className: 'tw-size-4 tw-text-neutral-800' })),
                React.createElement("p", { className: "tw-text-sm group-aria-checked:tw-font-semibold !tw-m-0" }, displayName)));
        }))));
};
